import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchSupportStats, SupportStats as Stats } from '../../hooks/chat/api';

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Card variant="outlined" sx={{ flex: '1 1 160px' }}>
      <CardContent sx={{ textAlign: 'center', '&:last-child': { pb: 2 } }}>
        <Typography variant="h4" sx={{ color }}>
          {value}
        </Typography>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

/** Session counts by status, straight from ant's queue counts. */
export function SupportStats() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStats(await fetchSupportStats());
      setFailed(false);
    } catch (_) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Conversations by status</Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              size="small"
              onClick={load}
              disabled={loading}
              aria-label="Refresh stats"
            >
              {loading ? (
                <CircularProgress size={16} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {!stats && loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {!stats && !loading && failed && (
        <Typography color="text.secondary">Could not load the stats.</Typography>
      )}
      {stats && (
        <Stack direction="row" gap={2} flexWrap="wrap">
          <StatCard label="Total" value={stats.totalConversations} color="primary.main" />
          <StatCard label="Open" value={stats.totalOpen} color="success.main" />
          <StatCard label="In progress" value={stats.totalInProgress} color="info.main" />
          <StatCard label="Closed" value={stats.totalClosed} color="neutral.500" />
        </Stack>
      )}
    </Box>
  );
}
